import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MediaPicker from './MediaPicker';
import useMedia from '../hooks/useMedia';

const BLOCK_TYPES = [
  { type: 'text',      label: 'Text',      icon: '¶' },
  { type: 'heading',   label: 'Heading',   icon: 'H' },
  { type: 'quote',     label: 'Quote',     icon: '❝' },
  { type: 'image',     label: 'Image',     icon: '🖼' },
  { type: 'container', label: 'Container', icon: '📦' },
  { type: 'divider',   label: 'Divider',   icon: '—' },
  { type: 'spacer',    label: 'Spacer',    icon: '↕' },
];

function createBlock(type) {
  const id = `block-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  switch (type) {
    case 'heading':   return { id, type: 'heading',   props: { level: 2, text: '' } };
    case 'quote':     return { id, type: 'quote',     props: { html: '' } };
    case 'image':     return { id, type: 'image',     props: { objectKey: '', alt: '', caption: '' } };
    case 'container': return { id, type: 'container', props: { layout: '1-col' }, children: [] };
    case 'divider':   return { id, type: 'divider',   props: {} };
    case 'spacer':    return { id, type: 'spacer',    props: { size: 'medium' } };
    default:          return { id, type: 'text',      props: { html: '' } };
  }
}

function updateBlock(block, updates) {
  return { ...block, props: { ...block.props, ...updates } };
}

/* ── Container Block (Nested Components) ── */
function ContainerBlock({ block, onChange, policy, readOnly }) {
  const children = Array.isArray(block.children) ? block.children : [];

  const handleChildrenChange = (newBody) => {
    onChange({
      ...block,
      children: newBody.blocks || [],
    });
  };

  return (
    <div
      className="block-container-wrapper"
      style={{
        border: '2px dashed #94a3b8',
        borderRadius: '12px',
        padding: '1rem',
        background: '#f8fafc',
        opacity: readOnly ? 0.75 : 1,
        pointerEvents: readOnly ? 'none' : 'auto',
      }}
    >
      <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, marginBottom: '0.5rem' }}>
        📦 Layout Container (Geschachtelte Blöcke: {children.length})
      </div>
      <BlockEditor
        value={{ blocks: children }}
        onChange={handleChildrenChange}
        policy={policy}
        readOnly={readOnly}
      />
    </div>
  );
}

function BlockRenderer({ block, onChange, focused, onFocus, onBlur, policy, readOnly }) {
  switch (block.type) {
    case 'heading':   return <HeadingBlock   block={block} onChange={onChange} readOnly={readOnly} />;
    case 'quote':     return <QuoteBlock     block={block} onChange={onChange} focused={focused} onFocus={onFocus} onBlur={onBlur} readOnly={readOnly} />;
    case 'image':     return <ImageBlock     block={block} onChange={onChange} readOnly={readOnly} />;
    case 'container': return <ContainerBlock block={block} onChange={onChange} policy={policy} readOnly={readOnly} />;
    case 'divider':   return <DividerBlock />;
    case 'spacer':    return <SpacerBlock    block={block} onChange={onChange} readOnly={readOnly} />;
    default:          return <TextBlock      block={block} onChange={onChange} focused={focused} onFocus={onFocus} onBlur={onBlur} readOnly={readOnly} />;
  }
}

/* ── Inline Block Inserter ── */
function BlockInserter({ onAdd, allowedTypes, readOnly }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  if (readOnly) return null;

  const availableTypes = Array.isArray(allowedTypes) && allowedTypes.length > 0
    ? BLOCK_TYPES.filter((b) => allowedTypes.includes(b.type))
    : BLOCK_TYPES;

  return (
    <div ref={ref} className={`block-inserter ${open ? 'open' : ''}`}>
      <button
        type="button"
        className="block-inserter-btn"
        onClick={() => setOpen((v) => !v)}
        title="Block hinzufügen"
        disabled={readOnly}
      >
        +
      </button>
      {open && (
        <div className="block-inserter-popup">
          <div className="block-type-grid">
            {availableTypes.map(({ type, label, icon }) => (
              <button
                key={type}
                type="button"
                className="block-type-btn"
                onClick={() => { onAdd(type); setOpen(false); }}
              >
                <span className="block-type-icon">{icon}</span>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Format Toolbar ── */
function FormatToolbar({ onFormat }) {
  return (
    <div className="format-toolbar" onMouseDown={(e) => e.preventDefault()}>
      <button type="button" className="format-btn" title="Bold"          onMouseDown={() => onFormat('bold')}><b>B</b></button>
      <button type="button" className="format-btn" title="Italic"        onMouseDown={() => onFormat('italic')}><i>I</i></button>
      <button type="button" className="format-btn" title="Underline"     onMouseDown={() => onFormat('underline')}><u>U</u></button>
      <span className="format-separator" />
      <button type="button" className="format-btn" title="Strikethrough" onMouseDown={() => onFormat('strikeThrough')} style={{ textDecoration: 'line-through' }}>S</button>
      <button type="button" className="format-btn" title="Link"          onMouseDown={() => onFormat('link')}>🔗</button>
      <button type="button" className="format-btn" title="Remove link"   onMouseDown={() => onFormat('unlink')}>✂︎</button>
    </div>
  );
}

/* ── Text Block ── */
function TextBlock({ block, onChange, focused, onFocus, onBlur, readOnly }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = block.props.html || '';
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const execFormat = (command) => {
    if (readOnly) return;
    if (command === 'link') {
      const url = window.prompt('URL eingeben:');
      if (url) document.execCommand('createLink', false, url);
    } else {
      document.execCommand(command, false, null);
    }
    if (ref.current) onChange(updateBlock(block, { html: ref.current.innerHTML }));
  };

  return (
    <div>
      {focused && !readOnly && <FormatToolbar onFormat={execFormat} />}
      <div
        ref={ref}
        className="rich-editor"
        contentEditable={!readOnly}
        suppressContentEditableWarning
        onFocus={onFocus}
        onBlur={() => {
          if (ref.current && !readOnly) onChange(updateBlock(block, { html: ref.current.innerHTML }));
          onBlur();
        }}
        onInput={(e) => {
          if (!readOnly) onChange(updateBlock(block, { html: e.currentTarget.innerHTML }));
        }}
        style={{ opacity: readOnly ? 0.7 : 1, cursor: readOnly ? 'not-allowed' : 'text' }}
      />
    </div>
  );
}

/* ── Quote Block ── */
function QuoteBlock({ block, onChange, focused, onFocus, onBlur, readOnly }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = block.props.html || '';
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const execFormat = (command) => {
    if (readOnly) return;
    if (command === 'link') {
      const url = window.prompt('URL eingeben:');
      if (url) document.execCommand('createLink', false, url);
    } else {
      document.execCommand(command, false, null);
    }
    if (ref.current) onChange(updateBlock(block, { html: ref.current.innerHTML }));
  };

  return (
    <blockquote className="block-quote">
      {focused && !readOnly && <FormatToolbar onFormat={execFormat} />}
      <div
        ref={ref}
        className="rich-editor"
        contentEditable={!readOnly}
        suppressContentEditableWarning
        onFocus={onFocus}
        onBlur={() => {
          if (ref.current && !readOnly) onChange(updateBlock(block, { html: ref.current.innerHTML }));
          onBlur();
        }}
        onInput={(e) => {
          if (!readOnly) onChange(updateBlock(block, { html: e.currentTarget.innerHTML }));
        }}
        style={{ opacity: readOnly ? 0.7 : 1, cursor: readOnly ? 'not-allowed' : 'text' }}
      />
    </blockquote>
  );
}

/* ── Heading Block ── */
function HeadingBlock({ block, onChange, readOnly }) {
  const level = Number(block.props.level) || 2;

  return (
    <div className="block-heading">
      <select
        value={level}
        onChange={(e) => onChange(updateBlock(block, { level: Number(e.target.value) }))}
        disabled={readOnly}
      >
        <option value={1}>H1</option>
        <option value={2}>H2</option>
        <option value={3}>H3</option>
        <option value={4}>H4</option>
      </select>
      <input
        type="text"
        placeholder="Überschrift…"
        value={block.props.text || ''}
        onChange={(e) => onChange(updateBlock(block, { text: e.target.value }))}
        disabled={readOnly}
      />
    </div>
  );
}

/* ── Image Block ── */
function ImageBlock({ block, onChange, readOnly }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const { getMediaItem } = useMedia();

  useEffect(() => {
    if (!block.props.mediaId) {
      setPreviewUrl('');
      return;
    }
    let cancelled = false;
    getMediaItem(block.props.mediaId)
      .then((res) => {
        if (!cancelled && res.item?.accessUrl) {
          setPreviewUrl(res.item.accessUrl || res.item.thumbnailUrl);
        }
      })
      .catch(() => setPreviewUrl(''));
    return () => {
      cancelled = true;
    };
  }, [block.props.mediaId, getMediaItem]);

  return (
    <div className="block-image">
      {previewUrl ? (
        <figure>
          <img src={previewUrl} alt={block.props.alt || ''} />
          <figcaption>
            <input
              type="text"
              value={block.props.caption || ''}
              onChange={(e) => onChange(updateBlock(block, { caption: e.target.value }))}
              placeholder="Bildunterschrift…"
              disabled={readOnly}
            />
          </figcaption>
        </figure>
      ) : (
        <button type="button" className="button secondary" onClick={() => setPickerOpen(true)} disabled={readOnly}>
          + Bild auswählen
        </button>
      )}
      <input
        type="text"
        value={block.props.alt || ''}
        onChange={(e) => onChange(updateBlock(block, { alt: e.target.value }))}
        placeholder="Alt-Text"
        style={{ marginTop: '0.5rem' }}
        disabled={readOnly}
      />
      {previewUrl && !readOnly && (
        <button type="button" className="button ghost small" onClick={() => setPickerOpen(true)} style={{ marginTop: '0.35rem' }}>
          Bild ändern
        </button>
      )}
      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(item) =>
          onChange(updateBlock(block, { mediaId: item.mediaId, objectKey: item.objectKey, alt: item.altText || block.props.alt }))
        }
      />
    </div>
  );
}

function DividerBlock() { return <hr style={{ margin: '0.5rem 0' }} />; }

function SpacerBlock({ block, onChange, readOnly }) {
  return (
    <div className="block-spacer">
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
        Abstand:
        <select
          value={block.props.size || 'medium'}
          onChange={(e) => onChange(updateBlock(block, { size: e.target.value }))}
          style={{ width: 'auto' }}
          disabled={readOnly}
        >
          <option value="small">Klein</option>
          <option value="medium">Mittel</option>
          <option value="large">Groß</option>
        </select>
      </label>
    </div>
  );
}

/* ── Main BlockEditor ── */
export default function BlockEditor({ value, onChange, policy, readOnly = false }) {
  const [focusedIndex, setFocusedIndex] = useState(null);

  const allowedTypes = policy?.allowedComponents || null;

  const blocks = useMemo(() => {
    if (value && Array.isArray(value.blocks)) return value.blocks;
    return [];
  }, [value]);

  const updateBlocks = useCallback((next) => onChange({ blocks: next }), [onChange]);

  const addBlock = useCallback((type, afterIndex) => {
    if (readOnly) return;
    const next = [...blocks];
    next.splice(afterIndex + 1, 0, createBlock(type));
    updateBlocks(next);
  }, [blocks, updateBlocks, readOnly]);

  const removeBlock = useCallback((index) => {
    if (readOnly) return;
    updateBlocks(blocks.filter((_, i) => i !== index));
    setFocusedIndex(null);
  }, [blocks, updateBlocks, readOnly]);

  const moveBlock = useCallback((index, dir) => {
    if (readOnly) return;
    const target = index + dir;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    [next[index], next[target]] = [next[target], next[index]];
    updateBlocks(next);
  }, [blocks, updateBlocks, readOnly]);

  const changeBlockType = useCallback((index, type) => {
    if (readOnly) return;
    const next = [...blocks];
    next[index] = createBlock(type);
    updateBlocks(next);
  }, [blocks, updateBlocks, readOnly]);

  const updateBlockAt = useCallback((index, nextBlock) => {
    if (readOnly) return;
    const next = [...blocks];
    next[index] = nextBlock;
    updateBlocks(next);
  }, [blocks, updateBlocks, readOnly]);

  return (
    <div className="block-editor" style={{ opacity: readOnly ? 0.75 : 1, pointerEvents: readOnly ? 'none' : 'auto' }}>
      {/* Inserter before first block */}
      <BlockInserter onAdd={(type) => addBlock(type, -1)} allowedTypes={allowedTypes} readOnly={readOnly} />

      {blocks.length === 0 && (
        <div className="block-empty">
          <p className="muted">Klicke "+" um deinen ersten Block hinzuzufügen.</p>
        </div>
      )}

      {blocks.map((block, index) => (
        <div key={block.id || index}>
          <div className="block-item">
            <div className="block-toolbar">
              <select
                className="small"
                value={block.type}
                onChange={(e) => changeBlockType(index, e.target.value)}
                disabled={readOnly}
              >
                {BLOCK_TYPES.map(({ type, label }) => <option key={type} value={type}>{label}</option>)}
              </select>
              <div className="block-actions">
                <button type="button" className="button ghost small" onClick={() => moveBlock(index, -1)} disabled={index === 0 || readOnly} title="Nach oben">↑</button>
                <button type="button" className="button ghost small" onClick={() => moveBlock(index, 1)}  disabled={index === blocks.length - 1 || readOnly} title="Nach unten">↓</button>
                <button type="button" className="button danger small" onClick={() => removeBlock(index)} disabled={readOnly} title="Entfernen">✕</button>
              </div>
            </div>
            <div className="block-content">
              <BlockRenderer
                block={block}
                onChange={(nextBlock) => updateBlockAt(index, nextBlock)}
                focused={focusedIndex === index}
                onFocus={() => setFocusedIndex(index)}
                onBlur={() => setFocusedIndex((prev) => (prev === index ? null : prev))}
                policy={policy}
                readOnly={readOnly}
              />
            </div>
          </div>
          {/* Inserter after each block */}
          <BlockInserter onAdd={(type) => addBlock(type, index)} allowedTypes={allowedTypes} readOnly={readOnly} />
        </div>
      ))}
    </div>
  );
}
